import { useCallback, useEffect, useState } from 'react';
import { useUndoableAction } from '../../components/useUndoableAction';
import { getErrorMessage } from '../../lib/errors';
import { type RefreshTopic, useRefreshOn } from '../../lib/resourceRefresh';
import type { CollectionApi } from './knowledgeBaseApi';

export interface DeferredRemoveOptions {
  /** Row to hide now and delete once the undo window closes. */
  id: string;
  /** Quoted in the undo toast: `"Spring blend" deleted`. */
  label: string;
  /** Section voice for a failed delete, e.g. 'That product could not be deleted.' */
  failureMessage: string;
  /**
   * Fired once the DELETE has actually gone through — after the undo window
   * closed, not when the row left the screen. Screens whose *other* data is
   * derived from these rows use it to re-read that data at the one moment it
   * really changed. Never called on undo, and never on failure.
   */
  onRemoved?: () => void;
}

export interface CollectionResult<TRead, TCreate, TUpdate> {
  /** Newest first, as returned by the list endpoint, minus pending deletes. */
  items: TRead[];
  isLoading: boolean;
  /** Load error only. Write errors are thrown to the caller instead. */
  error: string | null;
  /** A deferred delete whose API call failed. The row is back on screen. */
  deleteError: string | null;
  refresh: () => void;
  create: (body: TCreate) => Promise<TRead>;
  /**
   * Puts rows the server already created at the front of the list, newest-first
   * like the list endpoint, skipping any id that is on it already.
   *
   * The batch counterpart of `create`: an endpoint that writes several rows in
   * one call (following a batch of catalogue feeds) hands back the rows it made,
   * and this is how they reach the screens sharing this collection — without a
   * refetch, and without a second list to keep in step.
   */
  insertMany: (created: readonly TRead[]) => void;
  update: (id: string, body: TUpdate) => Promise<TRead>;
  /**
   * Puts a row the server already changed in place of its old self, by id.
   * For an action endpoint outside the CRUD shape (a planned post's reset)
   * that answers with the updated row: no refetch, no second list.
   */
  replace: (updated: TRead) => void;
  /** Immediate delete. Screens use `removeWithUndo`; this is the raw call. */
  remove: (id: string) => Promise<void>;
  /** Optimistic delete: hides the row now, deletes it when undo expires. */
  removeWithUndo: (options: DeferredRemoveOptions) => void;
}

interface Identified {
  id: string;
}

/** Result of the last completed load, tagged with the business it belongs to. */
interface LoadedCollection<TRead> {
  businessId: string;
  items: TRead[];
  error: string | null;
}

/** Stable empty result, so consumers do not see a new array identity per render. */
const NO_ITEMS: never[] = [];

/**
 * List + CRUD state for one knowledge-base collection.
 *
 * Loaded rows are stored tagged with their `business_id` and the visible state is
 * derived during render, so nothing is written to state synchronously inside the
 * effect and records from a previous business can never leak onto the screen.
 *
 * Writes resolve against the server response and patch local state in place, so
 * a save never costs a full refetch. Write failures throw so the calling form
 * can keep the user's input on screen.
 *
 * Deletes are the exception: `removeWithUndo` hides the row and hands the real
 * call to the undo host, which fires it once the window closes.
 *
 * `refreshOn` names the topic this list re-reads on when the rows change
 * elsewhere (the assistant wrote one, or undid a write): see `resourceRefresh`.
 * The re-read keeps the rows on screen until it lands.
 */
export function useCollection<TRead extends Identified, TCreate, TUpdate>(
  api: CollectionApi<TRead, TCreate, TUpdate>,
  businessId: string | null,
  refreshOn: RefreshTopic | null = null,
): CollectionResult<TRead, TCreate, TUpdate> {
  const [loaded, setLoaded] = useState<LoadedCollection<TRead> | null>(null);
  const [nonce, setNonce] = useState(0);
  const [pendingRemovalIds, setPendingRemovalIds] = useState<readonly string[]>([]);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const { schedule } = useUndoableAction();

  const refresh = useCallback(() => setNonce((value) => value + 1), []);

  useRefreshOn(businessId, refreshOn, refresh);

  useEffect(() => {
    if (!businessId) return;

    const controller = new AbortController();
    let cancelled = false;

    void (async () => {
      try {
        const items = await api.list(businessId, controller.signal);
        if (cancelled) return;
        setLoaded({ businessId, items, error: null });
      } catch (caught) {
        if (cancelled || controller.signal.aborted) return;
        setLoaded({
          businessId,
          items: [],
          error: getErrorMessage(caught, 'Could not load these records.'),
        });
      }
    })();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [api, businessId, nonce]);

  /** Applies a local edit, but only to the currently displayed business. */
  const patchItems = useCallback(
    (mutate: (items: TRead[]) => TRead[]) => {
      setLoaded((current) =>
        current === null || current.businessId !== businessId
          ? current
          : { ...current, items: mutate(current.items) },
      );
    },
    [businessId],
  );

  const create = useCallback(
    async (body: TCreate): Promise<TRead> => {
      if (!businessId) throw new Error('No business context yet.');
      const created = await api.create(businessId, body);
      patchItems((items) => [created, ...items]);
      return created;
    },
    [api, businessId, patchItems],
  );

  const insertMany = useCallback(
    (created: readonly TRead[]) => {
      if (created.length === 0) return;
      patchItems((items) => {
        const known = new Set(items.map((item) => item.id));
        const fresh = created.filter((item) => !known.has(item.id));
        return fresh.length === 0 ? items : [...fresh, ...items];
      });
    },
    [patchItems],
  );

  const update = useCallback(
    async (id: string, body: TUpdate): Promise<TRead> => {
      if (!businessId) throw new Error('No business context yet.');
      const updated = await api.update(businessId, id, body);
      patchItems((items) => items.map((item) => (item.id === id ? updated : item)));
      return updated;
    },
    [api, businessId, patchItems],
  );

  const replace = useCallback(
    (updated: TRead) => {
      patchItems((items) => items.map((item) => (item.id === updated.id ? updated : item)));
    },
    [patchItems],
  );

  const remove = useCallback(
    async (id: string): Promise<void> => {
      if (!businessId) throw new Error('No business context yet.');
      await api.remove(businessId, id);
      patchItems((items) => items.filter((item) => item.id !== id));
    },
    [api, businessId, patchItems],
  );

  const forgetPending = useCallback((id: string) => {
    setPendingRemovalIds((current) => current.filter((value) => value !== id));
  }, []);

  const removeWithUndo = useCallback(
    ({ id, label, failureMessage, onRemoved }: DeferredRemoveOptions) => {
      if (!businessId) return;
      setDeleteError(null);
      setPendingRemovalIds((current) => (current.includes(id) ? current : [...current, id]));

      schedule({
        label,
        onCommit: async () => {
          await api.remove(businessId, id);
          // Only now does the row leave local state for good.
          patchItems((items) => items.filter((item) => item.id !== id));
          forgetPending(id);
          onRemoved?.();
        },
        onUndo: () => forgetPending(id),
        onFailure: (caught) => {
          forgetPending(id);
          setDeleteError(getErrorMessage(caught, failureMessage));
        },
      });
    },
    [api, businessId, forgetPending, patchItems, schedule],
  );

  const isCurrent = loaded !== null && loaded.businessId === businessId;

  const loadedItems = isCurrent ? loaded.items : NO_ITEMS;

  // Pending deletes are filtered out during render, never written to state:
  // undo restores the row without a refetch, and the effect stays sync-free.
  // Left unmemoised on purpose — the React Compiler owns memoisation here.
  const items =
    pendingRemovalIds.length === 0
      ? loadedItems
      : loadedItems.filter((item) => !pendingRemovalIds.includes(item.id));

  return {
    items,
    isLoading: businessId !== null && !isCurrent,
    error: isCurrent ? loaded.error : null,
    deleteError,
    refresh,
    create,
    insertMany,
    update,
    replace,
    remove,
    removeWithUndo,
  };
}
